import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
    Box,
    Button as MuiButton,
    IconButton,
    InputAdornment,
    Link,
    TextField,
    Typography,
    withStyles
} from '@material-ui/core';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import PropTypes from 'prop-types';

import ViewScaffolding from '../common/ViewScaffolding';
import TwoFactorVerification from './TwoFactorVerification';
import authApi from '../../api/authApi';
import { getQueryParams, isValidURL } from '../../services/locationHelper';
import api, { links } from '../../collums-components/api/config';
import { loginViewStyles } from './styles';
import { useCookies } from 'react-cookie';
import { COLLUMS_DOMAIN, COOKIE_KEYS, LOCAL_STORAGE_KEYS } from '../../services/storageKeys';

function LoginView({ classes }) {
    const [form, setForm] = useState({ email: '', password: '' });
    const [errorMessage, setErrorMessage] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [twoFactorToken, setTwoFactorToken] = useState(null);
    const [twoFactorMethods, setTwoFactorMethods] = useState(null);
    const [activeSessions, setActiveSessions] = useState(null);
    // eslint-disable-next-line
    const [cookies, setCookies, removeCookie] = useCookies(['token']);
    const history = useHistory();
    const location = useLocation();
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        const params = getQueryParams(location);
        const urlMap = {
            calendar: links.calendarUrl,
            admin: links.adminUrl,
            employees: links.employeesUrl,
            marketing: links.marketingUrl,
            pos: links.posUrl,
            stock: links.stockUrl,
            reporting: links.reportingUrl,
            journey: links.journeyUrl
        };

        if (params.passive === 'true') {
            const interval = setInterval(() => {
                const match = document.cookie.match(/(?:^|;\s*)token=([^;]+)/);
                if (match) {
                    clearInterval(interval);
                    const token = match[1];
                    const url = urlMap[params.redirect] || urlMap.calendar;
                    window.location = `${url}?token=${token}`;
                }
            }, 1000);
            return () => clearInterval(interval);
        }

        removeCookie('token', { path: '/', domain: COLLUMS_DOMAIN });
        removeCookie('token', { path: '/' });
        localStorage.removeItem(LOCAL_STORAGE_KEYS.TOKEN_KEY);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.USER_KEY);

        // After clearing session, poll for re-login from another tab
        const interval = setInterval(() => {
            const match = document.cookie.match(/(?:^|;\s*)token=([^;]+)/);
            if (match && match[1]) {
                clearInterval(interval);
                const token = match[1];
                const url = urlMap[params.redirect] || urlMap.calendar;
                window.location = `${url}?token=${token}`;
            }
        }, 1000);
        return () => clearInterval(interval);
        // eslint-disable-next-line
    }, []);

    const redirect = React.useCallback(
        (token, permissions) => {
            let params = getQueryParams(location);
            let url;

            if (isValidURL(params.redirect) || (params.redirect && params.redirect.includes('http://localhost'))) {
                url = params.redirect;
            } else {
                const urlMap = {
                    calendar: links.calendarUrl,
                    admin: links.adminUrl,
                    employees: links.employeesUrl,
                    marketing: links.marketingUrl,
                    pos: links.posUrl,
                    stock: links.stockUrl,
                    reporting: links.reportingUrl,
                    journey: links.journeyUrl
                };

                if (permissions[params.redirect]) {
                    url = urlMap[params.redirect] || urlMap.calendar;
                } else {
                    if (permissions.calendar) {
                        url = urlMap.calendar;
                    } else if (permissions.admin) {
                        url = urlMap.admin;
                    } else if (permissions.employees) {
                        url = urlMap.employees;
                    } else if (permissions.marketing) {
                        url = urlMap.marketing;
                    } else if (permissions.pos) {
                        url = urlMap.pos;
                    } else if (permissions.reporting) {
                        url = urlMap.reporting;
                    } else if (permissions.stock) {
                        url = urlMap.stock;
                    } else {
                        url = urlMap.calendar;
                    }
                }
            }
            delete params.redirect;
            delete params.logout;
            url = new URL(url);
            const codifiedParams = new URLSearchParams(params).toString();
            window.location = codifiedParams
                ? `${url.origin}${url.pathname || ''}?${codifiedParams}`
                : `${url.origin}${url.pathname || ''}`;
        },
        [location]
    );

    // useEffect(() => {
    //     const doEffect = async () => {
    //         const params = getQueryParams(location);
    //         if (params.logout === 'true') {
    //             await authApi.forceLogout();
    //             removeCookie('token');
    //         } else {
    //             const token = cookies.token;
    //             if (!token) {
    //                 return;
    //             }
    //             api.config({ headers: { Authorization: token } });
    //             await authApi.getMe();
    //             redirect(token);
    //         }
    //     };
    //
    //     doEffect();
    //     // eslint-disable-next-line
    // }, [location, redirect]);

    const submit = async () => {
        setSubmitted(true);
        if (!form.email || !form.password) {
            return;
        }
        try {
            const environment = window.location.hostname === 'localhost' ? {} : { path: '/', domain: COLLUMS_DOMAIN };
            const result = await authApi.login(form);

            if (result.requires2FA) {
                setTwoFactorToken(result.twoFactorToken);
                setTwoFactorMethods(result.twoFactorMethods || {});
                return;
            }

            api.config({ headers: { Authorization: result.token } });
            localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN_KEY, result.token);
            localStorage.setItem(LOCAL_STORAGE_KEYS.USER_KEY, JSON.stringify(result.user));
            setCookies(COOKIE_KEYS.TOKEN, result.token, environment);
            setCookies(COOKIE_KEYS.EXP_TIME, result.autoLogout?.expTime, environment);
            setCookies(COOKIE_KEYS.LOGOUT_ACTIVE, result.autoLogout?.logoutActive, environment);
            setCookies('isLocked', false, environment);
            const jobs = await authApi.getJobs();
            const permissions = jobs.find(job => job.id === result.user.job)?.permissions || {};
            redirect(result.token, permissions);
        } catch (error) {
            if (error && error.status === 403 && error.data?.sessions) {
                setErrorMessage(error.data.message);
                setActiveSessions(error.data.sessions);
            } else if (error && error.status === 403 && error.data?.message) {
                setErrorMessage(error.data.message);
            } else if (error && 401 === error.status) {
                if ('Please check your email for verify!' === error.data.message) {
                    setErrorMessage('This email address is not verified. Check your email for verify!');
                } else {
                    setErrorMessage('Invalid email or password');
                }
            } else {
                setErrorMessage('Something went wrong');
            }
        }
    };

    const change = (property, value) => {
        setForm({ ...form, [property]: value });
        setErrorMessage(null);
        setActiveSessions(null);
    };

    const handleDeleteSession = async sessionId => {
        try {
            await authApi.deleteSession({ email: form.email, password: form.password, sessionId });
            setActiveSessions(prev => prev.filter(s => s._id !== sessionId));
            setErrorMessage(null);
        } catch (e) {
            setErrorMessage('Failed to delete session');
        }
    };

    const handleTwoFactorVerified = async result => {
        const environment = window.location.hostname === 'localhost' ? {} : { path: '/', domain: COLLUMS_DOMAIN };
        api.config({ headers: { Authorization: result.token } });
        localStorage.setItem(LOCAL_STORAGE_KEYS.TOKEN_KEY, result.token);
        localStorage.setItem(LOCAL_STORAGE_KEYS.USER_KEY, JSON.stringify(result.user));
        setCookies(COOKIE_KEYS.TOKEN, result.token, environment);
        setCookies(COOKIE_KEYS.EXP_TIME, result.autoLogout?.expTime, environment);
        setCookies(COOKIE_KEYS.LOGOUT_ACTIVE, result.autoLogout?.logoutActive, environment);
        setCookies('isLocked', false, environment);
        const jobs = await authApi.getJobs();
        const permissions = jobs.find(job => job.id === result.user.job)?.permissions || {};
        redirect(result.token, permissions);
    };

    if (twoFactorToken) {
        return (
            <TwoFactorVerification
                twoFactorToken={twoFactorToken}
                twoFactorMethods={twoFactorMethods}
                onVerified={handleTwoFactorVerified}
                onBack={() => setTwoFactorToken(null)}
            />
        );
    }

    return (
        <ViewScaffolding
            form={
                <form>
                    <TextField
                        id="email-input"
                        classes={{ root: classes.textField }}
                        InputLabelProps={{ className: classes.input }}
                        color="primary"
                        size="small"
                        label="Email"
                        value={form.email}
                        onChange={event => change('email', event.target.value)}
                        onKeyDown={event => 13 === event.keyCode && submit()}
                        helperText={!form.email && submitted && 'A value must be provided'}
                        error={!form.email && submitted}
                    />
                    <TextField
                        id="password-input"
                        size="small"
                        color="primary"
                        classes={{ root: classes.textField }}
                        InputLabelProps={{ className: classes.input }}
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        value={form.password}
                        onChange={event => change('password', event.target.value)}
                        onKeyDown={event => 13 === event.keyCode && submit()}
                        helperText={!form.password && submitted && 'A value must be provided'}
                        error={!form.password && submitted}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(prev => !prev)} edge="end">
                                        {showPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </form>
            }
            link={
                <Link onClick={() => history.push('/forgot-password')}>
                    <Typography className={classes.link}>Forgot password</Typography>
                </Link>
            }
            action={submit}
            label="Log in"
            errorMessage={errorMessage}
            extra={
                activeSessions &&
                activeSessions.length > 0 && (
                    <Box mt={2}>
                        <Typography style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                            Active sessions:
                        </Typography>
                        {activeSessions.map(session => {
                            const deviceName = (() => {
                                const { deviceModel, deviceVendor, os } = session;
                                if (deviceModel === 'iPhone' || deviceModel === 'iPad') return deviceModel;
                                if (deviceModel && deviceVendor && !deviceModel.startsWith(deviceVendor)) {
                                    return `${deviceVendor} ${deviceModel}`;
                                }
                                if (deviceModel) return deviceModel;
                                if (deviceVendor === 'Apple') return 'Mac';
                                if (os) return os.split(' ')[0];
                                return null;
                            })();
                            const browserOs = session.os || null;
                            return (
                                <Box
                                    key={session._id}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    py={0.5}
                                    style={{ borderBottom: '1px solid #e0e0e0' }}
                                >
                                    <Box>
                                        {deviceName && (
                                            <Typography style={{ fontSize: 12, fontWeight: 600 }}>
                                                {deviceName}
                                                {session.deviceType && session.deviceType !== 'desktop'
                                                    ? ` (${session.deviceType})`
                                                    : ''}
                                            </Typography>
                                        )}
                                        {browserOs && (
                                            <Typography style={{ fontSize: 11, color: '#888' }}>{browserOs}</Typography>
                                        )}
                                        <Typography style={{ fontSize: 11, color: '#888' }}>
                                            Last active: {new Date(session.lastUsedAt).toLocaleString()}
                                        </Typography>
                                    </Box>
                                    <MuiButton
                                        size="small"
                                        style={{
                                            fontSize: 12,
                                            color: '#d32f2f',
                                            minWidth: 'auto',
                                            textTransform: 'none'
                                        }}
                                        onClick={() => handleDeleteSession(session._id)}
                                    >
                                        Log out
                                    </MuiButton>
                                </Box>
                            );
                        })}
                    </Box>
                )
            }
        />
    );
}

LoginView.propTypes = {
    classes: PropTypes.object.isRequired
};

export default withStyles(loginViewStyles)(LoginView);
